"use client";

import { JSX, useEffect, useRef } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import type { ProcessStatus } from "@/components/charts/process-status";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  legendIndexOfCategory,
  resolveCategoryColor,
  resolveCategoryLabel,
} from "./shared/category-resolve";
import type { ModuleCategory } from "@/types/modules";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendProgress,
  LegendValue,
} from "@/components/charts/legend";
import { importsTokenScope } from "./imports-token-scope";

export type ParallelStreamStep = {
  name: string;
  pos: number;
  status?: ProcessStatus;
  categoryId?: string;
};

export type ParallelStreamTrack = {
  id: string;
  name: string;
  color: string;
  steps: ParallelStreamStep[];
};

export type ParallelStreamMilestone = {
  pos: number;
  label: string;
  subLabel?: string;
};

export type ParallelStreamsData = {
  tracks: ParallelStreamTrack[];
  milestones?: ParallelStreamMilestone[];
  categories?: ModuleCategory[];
};

type StepRef = { tid: string; sidx: number };

const DEFAULT_DATA: ParallelStreamsData = {
  tracks: [
    {
      id: "frontend",
      name: "Frontend",
      color: "var(--chart-1)",
      steps: [
        { name: "Setup", pos: 5, status: "done" },
        { name: "Components", pos: 22, status: "done" },
        { name: "Integration", pos: 48, status: "active" },
        { name: "Polish", pos: 75, status: "pending" },
        { name: "Ship", pos: 95, status: "pending" },
      ],
    },
    {
      id: "backend",
      name: "Backend",
      color: "var(--chart-2)",
      steps: [
        { name: "API design", pos: 8, status: "done" },
        { name: "Endpoints", pos: 30, status: "done" },
        { name: "Auth", pos: 52, status: "active" },
        { name: "Migration", pos: 72, status: "pending" },
        { name: "Deploy", pos: 92, status: "pending" },
      ],
    },
    {
      id: "infra",
      name: "Infra",
      color: "var(--chart-3)",
      steps: [
        { name: "Terraform", pos: 12, status: "done" },
        { name: "CI/CD", pos: 35, status: "done" },
        { name: "Monitoring", pos: 62, status: "active" },
        { name: "Backups", pos: 90, status: "pending" },
      ],
    },
  ],
  milestones: [
    { pos: 25, label: "M1", subLabel: "Kick-off" },
    { pos: 55, label: "M2", subLabel: "Alpha" },
    { pos: 95, label: "M3", subLabel: "Launch" },
  ],
};

export default function ModuleParallelStreams({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: ParallelStreamsData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const statusLabel = (status: ProcessStatus | undefined): string =>
    status === "done"
      ? t("done")
      : status === "active"
        ? t("active")
        : status === "pending"
          ? t("pending")
          : "";
  const tracks = data.tracks ?? [];
  const milestones = data.milestones ?? [];
  const categories = data.categories;
  const free = Boolean(categories && categories.length > 0);
  const selection = useSelectionGroup();
  const step = useHoverSelect<StepRef>({
    eq: (a, b) => a.tid === b.tid && a.sidx === b.sidx,
    group: selection,
  });
  const effectiveStep = step.effective;
  const trackLegend = useLegendHoverSelect({ group: selection });
  const categoryLegend = useLegendHoverSelect({ group: selection });
  const maskId = useSvgId("ps-mask");
  const linePrefix = useSvgId("ps-line");
  const hoverGesture = useHoverGesture();

  const W = 760;
  const trackH = 58;
  const padL = 90;
  const padR = 32;
  const yHeader = 24;
  const yTracksTop = 62;
  const H = yTracksTop + tracks.length * trackH + 12;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [tracks, milestones]);
  const chartW = W - padL - padR;
  const xFor = (pos: number) => padL + (pos / 100) * chartW;

  const allSteps = tracks.flatMap((t) => t.steps);
  const legendItems = tracks.map((t) => {
    const done = t.steps.filter((s) => s.status === "done").length;
    return free
      ? {
          id: t.id,
          label: stripInlineMarkdown(t.name),
          color: t.color,
          value: t.steps.length,
        }
      : {
          id: t.id,
          label: stripInlineMarkdown(t.name),
          color: t.color,
          value: done,
          maxValue: t.steps.length,
        };
  });
  const categoryLegendItems = free
    ? (categories as ModuleCategory[]).map((cat) => ({
        id: cat.id,
        label: cat.label,
        color: cat.color,
        value: allSteps.filter((s) => s.categoryId === cat.id).length,
      }))
    : [];
  const catLegFilter =
    categoryLegend.effective !== null
      ? categoryLegendItems[categoryLegend.effective]?.id ?? null
      : null;
  const legHoveredTrackId =
    trackLegend.effective !== null
      ? legendItems[trackLegend.effective]?.id ?? null
      : null;

  const selectedStep = step.selected;
  const selectedStepCategoryId = selectedStep
    ? tracks.find((t) => t.id === selectedStep.tid)?.steps[selectedStep.sidx]
        ?.categoryId ?? null
    : null;
  const trackLegendSelectedIndex =
    trackLegend.selected ??
    legendIndexOfCategory(legendItems, selectedStep?.tid ?? null);
  const categoryLegendSelectedIndex =
    categoryLegend.selected ??
    legendIndexOfCategory(categoryLegendItems, selectedStepCategoryId);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activeStepInfo = effectiveStep
    ? (() => {
        const track = tracks.find((tr) => tr.id === effectiveStep.tid);
        const s = track?.steps[effectiveStep.sidx];
        return track && s ? { track, step: s } : null;
      })()
    : null;
  // All three surfaces feed one scroll target: a track row centres that track's
  // first step, a category row the first step carrying it, and a step picked in
  // a track is only pulled in when it sits outside the viewport. Keying the
  // effect on the resolved selector instead of the rebuilt arrays keeps it from
  // firing on unrelated renders, which is what makes re-picking the same row
  // scroll again.
  const scrollFromElement =
    trackLegend.selected === null &&
    categoryLegend.selected === null &&
    selectedStep !== null;
  const scrollSelector = (() => {
    if (trackLegend.selected !== null) {
      const trackId = legendItems[trackLegend.selected]?.id;
      return trackId ? `[data-part-index^="${trackId}:"]` : null;
    }
    if (categoryLegend.selected !== null) {
      const catId = categoryLegendItems[categoryLegend.selected]?.id;
      if (!catId) return null;
      for (const t of tracks) {
        const sidx = t.steps.findIndex((st) => st.categoryId === catId);
        if (sidx >= 0) return `[data-part-index="${t.id}:${sidx}"]`;
      }
      return null;
    }
    if (selectedStep !== null) {
      return `[data-part-index="${selectedStep.tid}:${selectedStep.sidx}"]`;
    }
    return null;
  })();
  const scrollAlign = scrollFromElement ? "nearest" : "center";
  useEffect(() => {
    if (scrollSelector === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(container, container.querySelector(scrollSelector), {
      align: scrollAlign,
      once: false,
    });
  }, [scrollSelector, scrollAlign]);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <g mask={`url(#${maskId})`}>
            {milestones.map((m) => (
              <line
                key={m.label}
                x1={xFor(m.pos)}
                x2={xFor(m.pos)}
                y1={yHeader + 16}
                y2={H - 6}
                stroke="var(--grid-strong)"
                strokeDasharray="3 4"
                strokeWidth="1"
              />
            ))}
          </g>
          {milestones.map((m) => (
            <g key={`ml-${m.label}`} transform={`translate(${xFor(m.pos)}, ${yHeader})`}>
              <text
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="var(--foreground)"
              >
                {stripInlineMarkdown(m.label)}
              </text>
              {m.subLabel && (
                <text
                  textAnchor="middle"
                  y="14"
                  fontSize="10"
                  fill="var(--subtle)"
                >
                  {stripInlineMarkdown(m.subLabel)}
                </text>
              )}
            </g>
          ))}

          {tracks.map((track, tIdx) => {
            const yLine = yTracksTop + tIdx * trackH + trackH / 2;
            const lastProg = [...track.steps]
              .reverse()
              .find((s) => s.status === "done" || s.status === "active");
            const progressEnd = free
              ? track.steps.reduce((m, s) => Math.max(m, s.pos), 0)
              : lastProg
                ? lastProg.pos
                : 0;
            const trackDim =
              legHoveredTrackId !== null && legHoveredTrackId !== track.id;

            return (
              <g
                key={track.id}
                opacity={trackDim ? 0.3 : 1}
                style={{ transition: "opacity 0.15s" }}
              >
                <foreignObject
                  x={4}
                  y={yLine - 12}
                  width={padL - 16}
                  height={24}
                  overflow="visible"
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      height: "100%",
                      color: "var(--muted-foreground)",
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: 1.15,
                      textAlign: "right",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {stripInlineMarkdown(track.name)}
                  </div>
                </foreignObject>

                <defs>
                  <linearGradient
                    id={`${linePrefix}-${track.id}`}
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                  >
                    <stop offset="0" stopColor={track.color} stopOpacity="0" />
                    <stop offset="0.05" stopColor={track.color} stopOpacity="1" />
                    <stop offset="1" stopColor={track.color} stopOpacity="1" />
                  </linearGradient>
                </defs>

                <line
                  x1={xFor(0)}
                  y1={yLine}
                  x2={xFor(progressEnd)}
                  y2={yLine}
                  stroke={`url(#${linePrefix}-${track.id})`}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <line
                  x1={xFor(progressEnd)}
                  y1={yLine}
                  x2={xFor(100)}
                  y2={yLine}
                  stroke={track.color}
                  strokeOpacity="0.35"
                  strokeWidth="1.5"
                  strokeDasharray="2 5"
                  strokeLinecap="round"
                />

                {track.steps.map((s, sIdx) => {
                  const cx = xFor(s.pos);
                  const isDone = !free && s.status === "done";
                  const isActive = !free && s.status === "active";
                  const isPending = !free && s.status === "pending";
                  const stepColor = free
                    ? resolveCategoryColor(s.categoryId, categories, track.color)
                    : track.color;
                  const isHovered =
                    effectiveStep?.tid === track.id && effectiveStep?.sidx === sIdx;
                  const dim =
                    (effectiveStep !== null && !isHovered) ||
                    (free && catLegFilter !== null && catLegFilter !== s.categoryId);

                  return (
                    <g
                      key={sIdx}
                      ref={(el) => {
                        anchorRefs.current[`${track.id}:${sIdx}`] = el;
                      }}
                      data-module-type="parallel-streams"
                      data-part-index={`${track.id}:${sIdx}`}
                      data-part-label={s.name}
                      onPointerEnter={(e) => {
                        if (hoverGesture.enter(e))
                          step.setHovered({ tid: track.id, sidx: sIdx });
                      }}
                      onPointerLeave={(e) => {
                        if (hoverGesture.leave(e)) step.setHovered(null);
                      }}
                      onClick={(e) =>
                        step.select(
                          { tid: track.id, sidx: sIdx },
                          {
                            kind: "step",
                            index: sIdx,
                            group: track.id,
                            element: e.currentTarget,
                          },
                        )
                      }
                      style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                      opacity={dim ? 0.35 : 1}
                    >
                      <circle cx={cx} cy={yLine} r={14} fill="transparent" />
                      {isHovered && !isActive && (
                        <circle
                          cx={cx}
                          cy={yLine}
                          r={8}
                          fill="none"
                          stroke={stepColor}
                          strokeWidth="1.3"
                        />
                      )}
                      {free && (
                        <circle cx={cx} cy={yLine} r={4} fill={stepColor} />
                      )}
                      {(isDone || isActive) && (
                        <circle cx={cx} cy={yLine} r={4} fill={track.color} />
                      )}
                      {isPending && (
                        <circle
                          cx={cx}
                          cy={yLine}
                          r={3}
                          fill="var(--background)"
                          stroke={track.color}
                          strokeWidth="1.3"
                          strokeOpacity="0.55"
                        />
                      )}
                      {isHovered && (
                        <text
                          x={cx}
                          y={yLine - 14}
                          textAnchor="middle"
                          fontSize="10.5"
                          fill="var(--foreground)"
                        >
                          {stripInlineMarkdown(s.name)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeStepInfo !== null}
        anchor={
          effectiveStep
            ? anchorRefs.current[`${effectiveStep.tid}:${effectiveStep.sidx}`] ??
              null
            : null
        }
        contentKey={
          effectiveStep
            ? `${effectiveStep.tid}:${effectiveStep.sidx}`
            : undefined
        }
        title={activeStepInfo?.step.name}
        rows={
          activeStepInfo
            ? [
                { color: activeStepInfo.track.color, label: t("Track"), value: activeStepInfo.track.name },
                {
                  color: free
                    ? resolveCategoryColor(
                        activeStepInfo.step.categoryId,
                        categories,
                        "var(--muted-foreground)",
                      )
                    : "var(--muted-foreground)",
                  label: t("Status"),
                  value: free
                    ? resolveCategoryLabel(
                        activeStepInfo.step.categoryId,
                        categories,
                        "",
                      )
                    : statusLabel(activeStepInfo.step.status),
                },
                { color: "var(--muted-foreground)", label: t("Position"), value: `${activeStepInfo.step.pos}%` },
              ]
            : []
        }
      />

      {showLegend && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={trackLegend.hovered}
            onHoverChange={trackLegend.setHovered}
            selectedIndex={trackLegendSelectedIndex}
            onSelect={trackLegend.select}
            onLabelRef={trackLegend.setLabelRef}
            className="flex flex-row gap-2"
          >
            {free ? (
              <LegendItem className="flex shrink-0 items-center gap-2 px-3 py-2">
                <LegendMarker />
                <LegendLabel />
                <LegendValue />
              </LegendItem>
            ) : (
              <LegendItem className="flex shrink-0 flex-col gap-1.5 px-3 py-2">
                <div className="flex items-center gap-2">
                  <LegendMarker />
                  <LegendLabel />
                  <span className="ml-auto">
                    <LegendValue showPercentage />
                  </span>
                </div>
                <LegendProgress />
              </LegendItem>
            )}
          </Legend>
        </SwipeScroll>
      )}

      {showLegend && free && (
        <SwipeScroll>
          <Legend
            items={categoryLegendItems}
            hoveredIndex={categoryLegend.hovered}
            onHoverChange={categoryLegend.setHovered}
            selectedIndex={categoryLegendSelectedIndex}
            onSelect={categoryLegend.select}
            onLabelRef={categoryLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
